import '../AdminDetailOverview.scss';
import './RecruitSeoReview.scss';
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';

// 교회 인증 관리 (in-system-apply-strategy.md 7단계)
// 공고 동기화로 만들어진 교회 프로필 중 실제 교회임을 확인한 곳에 '인증 교회' 배지를 붙인다.
// 배지는 공고 상세의 지원 패널(웹·앱)과 교회 프로필 페이지에 보인다.

interface ChurchRow {
  id: number;
  name: string;
  religiousbody: string | null;
  region: string;
  mainPastor: string | null;
  address: string | null;
  homepage: string | null;
  isVerified: boolean;
  postCount: number;
}

const FILTERS: [string, string][] = [['', '전체'], ['1', '인증됨'], ['0', '미인증']];

export default function ChurchVerifyManage() {
  const [items, setItems] = useState<ChurchRow[]>([]);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(30);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [verified, setVerified] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${MainURL}/recruitseoadmin/churches`, { params: { page, search: query, verified } });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setPageSize(res.data.pageSize || 30);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || '교회 목록을 불러오지 못했습니다.');
    }
  }, [page, query, verified]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (row: ChurchRow) => {
    if (busyId) return;
    const next = !row.isVerified;
    if (!window.confirm(next ? `'${row.name}'을(를) 인증 교회로 표시할까요?` : `'${row.name}'의 인증을 해제할까요?`)) return;
    setBusyId(row.id);
    try {
      await axios.post(`${MainURL}/recruitseoadmin/churches/verify`, { id: row.id, verified: next });
      setItems(prev => prev.map(i => (i.id === row.id ? { ...i, isVerified: next } : i)));
    } catch (err: any) {
      alert(err.response?.data?.error || '저장하지 못했습니다.');
    } finally {
      setBusyId(null);
    }
  };

  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="recruit-seo-review service-detail-overview">
      <p className="service-detail-overview__hint">
        담임목사·주소·홈페이지 등으로 실제 교회임을 확인한 곳만 인증해 주세요. 인증 교회는 공고 상세의 지원 영역과 교회 프로필에 <b>인증 교회</b> 배지가 붙습니다.
      </p>

      <div className="service-detail-overview__tabs">
        {FILTERS.map(([key, label]) => (
          <button
            type="button"
            key={key || 'all'}
            className={`service-detail-overview__tab${verified === key ? ' is-active' : ''}`}
            onClick={() => { setVerified(key); setPage(1); }}
          >
            {label}
          </button>
        ))}
      </div>

      <form
        className="church-verify__search"
        onSubmit={(e) => { e.preventDefault(); setQuery(search.trim()); setPage(1); }}
      >
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="교회명 · 담임목사 · 주소" />
        <button type="submit">검색</button>
      </form>

      {error && <p className="church-verify__empty">{error}</p>}
      {!error && items.length === 0 && <p className="church-verify__empty">교회가 없습니다.</p>}

      <table className="church-verify__table">
        <thead>
          <tr>
            <th>교회</th>
            <th>지역</th>
            <th>담임목사</th>
            <th>공고</th>
            <th>인증</th>
          </tr>
        </thead>
        <tbody>
          {items.map(row => (
            <tr key={row.id}>
              <td>
                <a href={`/church/${row.id}`} target="_blank" rel="noopener noreferrer">{row.name}</a>
                {row.religiousbody && <div className="church-verify__sub">{row.religiousbody}</div>}
                {row.address && <div className="church-verify__sub">{row.address}</div>}
                {row.homepage && (
                  <div className="church-verify__sub">
                    <a href={/^https?:\/\//.test(row.homepage) ? row.homepage : `http://${row.homepage}`} target="_blank" rel="noopener noreferrer">{row.homepage}</a>
                  </div>
                )}
              </td>
              <td>{row.region}</td>
              <td>{row.mainPastor}</td>
              <td>{row.postCount}</td>
              <td>
                <button
                  type="button"
                  className={`church-verify__toggle${row.isVerified ? ' is-on' : ''}`}
                  disabled={busyId === row.id}
                  onClick={() => toggle(row)}
                >
                  {row.isVerified ? '인증됨' : '인증하기'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="church-verify__pager">
        <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>이전</button>
        <span>{page} / {lastPage} · 총 {total}곳</span>
        <button type="button" disabled={page >= lastPage} onClick={() => setPage(page + 1)}>다음</button>
      </div>
    </div>
  );
}
